import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router';
import { Button, Input } from '@artosera/ui';
import { CLE_SESSION, useSession } from '../auth/useSession.js';
import {
  ApiError,
  confirmerDeuxFacteurs,
  enrolerDeuxFacteurs,
  regenererCodesSecours,
} from '../lib/api.js';
import { leverConfinement } from '../lib/confinement.js';
import formStyles from '../styles/form.module.css';
import pageStyles from '../styles/page.module.css';
import styles from './Securite.module.css';

type Etape = 'repos' | 'scan' | 'codes' | 'regeneration';

/**
 * Sécurité du compte (ARTO-58) : l'enrôlement du second facteur, depuis la
 * console. Le parcours est celui de la régie (`ReglagesSecuritePage`) :
 * `POST /v1/auth/2fa/enroll` rend une URI `otpauth://` — aucune image n'est
 * produite côté API — et le QR se dessine ici, avec `qrcode` ; `/confirm`
 * active le facteur sur un code valide et rend les codes de secours, une
 * seule fois.
 *
 * Ouverte à tout rôle de console, associé compris, et SEUL écran d'une
 * session confinée à l'enrôlement (`RequireConsoleAuth`) : l'associé invité
 * n'a pas d'autre moyen de s'enrôler.
 */
export function Securite(): JSX.Element {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { moi, confine } = useSession();
  const actif = moi?.compte.deuxiemeFacteurActive === true;

  const [etape, setEtape] = useState<Etape>('repos');
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codesSecours, setCodesSecours] = useState<readonly string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function tenter(geste: () => Promise<void>): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      await geste();
    } catch (refus) {
      const codeRefuse =
        refus instanceof ApiError &&
        (refus.details as { raison?: unknown } | null)?.raison === 'mfa_code_invalide';
      setErreur(codeRefuse ? t('securite.erreurCode') : t('securite.erreurGenerique'));
    } finally {
      setEnCours(false);
    }
  }

  const demarrer = (): Promise<void> =>
    tenter(async () => {
      const reponse = await enrolerDeuxFacteurs();
      setSecret(reponse.secret);
      setQr(await QRCode.toDataURL(reponse.otpauth, { margin: 1, width: 192 }));
      setCode('');
      setEtape('scan');
    });

  const confirmer = (event: FormEvent): Promise<void> => {
    event.preventDefault();
    return tenter(async () => {
      const reponse = await confirmerDeuxFacteurs(code.trim());
      setCodesSecours(reponse.codesSecours);
      setCode('');
      setSecret(null);
      setQr(null);
      setEtape('codes');
    });
  };

  const regenerer = (event: FormEvent): Promise<void> => {
    event.preventDefault();
    return tenter(async () => {
      const reponse = await regenererCodesSecours(code.trim());
      setCodesSecours(reponse.codesSecours);
      setCode('');
      setEtape('codes');
    });
  };

  /** Les codes sont notés : la session est relue, et la console se rouvre. */
  async function continuer(): Promise<void> {
    setCodesSecours([]);
    setEtape('repos');
    leverConfinement();
    await queryClient.invalidateQueries({ queryKey: CLE_SESSION });
    if (confine || !actif) navigate('/vue-d-aigle', { replace: true });
  }

  const champCode = (id: string): JSX.Element => (
    <div className={formStyles.champ}>
      <label htmlFor={id}>{t('securite.champCode')}</label>
      <Input
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        required
        mono
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('securite.titre')}</h1>
          <p>{t('securite.sousTitre')}</p>
        </div>
      </div>

      {confine ? (
        <p className={pageStyles.banniere} role="status">
          {t('securite.confine')}
        </p>
      ) : moi?.enrolementMfaRequis && etape === 'repos' ? (
        <p className={pageStyles.banniere}>{t('securite.grace')}</p>
      ) : null}

      <section className={pageStyles.panneau} aria-labelledby="securite-statut">
        <h2 id="securite-statut" className={styles.statut}>
          {actif ? t('securite.statutActif') : t('securite.statutInactif')}
        </h2>

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        {etape === 'repos' && !actif ? (
          <Button loading={enCours} onClick={() => void demarrer()}>
            {t('securite.demarrer')}
          </Button>
        ) : null}

        {etape === 'scan' ? (
          <div className={styles.scan}>
            <div>
              <h3 className={styles.sousTitre}>{t('securite.etape1Titre')}</h3>
              <p className={styles.texte}>{t('securite.etape1Texte')}</p>
              {qr ? (
                <img
                  className={styles.qr}
                  src={qr}
                  alt={t('securite.qrAlt')}
                  width={192}
                  height={192}
                />
              ) : null}
              {secret ? (
                <p className={styles.cle}>
                  <span>{t('securite.cleManuelle')}</span>
                  <code>{secret}</code>
                </p>
              ) : null}
            </div>
            <form onSubmit={(event) => void confirmer(event)}>
              <h3 className={styles.sousTitre}>{t('securite.etape2Titre')}</h3>
              {champCode('securite-code-enrolement')}
              <div className={styles.actions}>
                <Button type="submit" loading={enCours}>
                  {t('securite.confirmer')}
                </Button>
                <Button variant="ghost" type="button" onClick={() => setEtape('repos')}>
                  {t('securite.annuler')}
                </Button>
              </div>
            </form>
          </div>
        ) : null}

        {etape === 'codes' ? (
          <div className={styles.codes}>
            <h3 className={styles.sousTitre}>{t('securite.codesTitre')}</h3>
            <p className={styles.texte}>{t('securite.codesTexte')}</p>
            <ul className={styles.listeCodes} aria-label={t('securite.codesListe')}>
              {codesSecours.map((valeur) => (
                <li key={valeur}>{valeur}</li>
              ))}
            </ul>
            <Button onClick={() => void continuer()}>
              {actif && !confine ? t('securite.termine') : t('securite.continuer')}
            </Button>
          </div>
        ) : null}

        {etape === 'repos' && actif ? (
          <div className={styles.codes}>
            <h3 className={styles.sousTitre}>{t('securite.regenererTitre')}</h3>
            <p className={styles.texte}>{t('securite.regenererTexte')}</p>
            <Button variant="secondary" onClick={() => setEtape('regeneration')}>
              {t('securite.regenerer')}
            </Button>
          </div>
        ) : null}

        {etape === 'regeneration' ? (
          <form className={styles.codes} onSubmit={(event) => void regenerer(event)}>
            <h3 className={styles.sousTitre}>{t('securite.regenererTitre')}</h3>
            <p className={styles.texte}>{t('securite.regenererTexte')}</p>
            {champCode('securite-code-regeneration')}
            <div className={styles.actions}>
              <Button type="submit" loading={enCours}>
                {t('securite.regenerer')}
              </Button>
              <Button variant="ghost" type="button" onClick={() => setEtape('repos')}>
                {t('securite.annuler')}
              </Button>
            </div>
          </form>
        ) : null}
      </section>
    </div>
  );
}
